import { motion } from "motion/react";
import type React from "react";

import { AskNobiChatLogo } from "@/components/AskNobi/AskNobiChatLogo";
import { Spinner } from "@/design-system/Spinner";
import { useTranslation } from "@/translations";

import type { AskNobiPipelineStage } from "./helpers/types";

interface AskNobiChatMessageThinkingProps {
  connecting: boolean;
  pipelineStage: AskNobiPipelineStage | undefined;
}

function usePipelineStageLabel(stage: AskNobiPipelineStage | undefined): string {
  const { t } = useTranslation();

  switch (stage) {
    case "planning":
      return t("component.ai.stage.planning");
    case "fetching":
      return t("component.ai.stage.fetching");
    case "evaluating":
      return t("component.ai.stage.evaluating");
    case "generating":
      return t("component.ai.stage.generating");
    default:
      return t("component.ai.thinking");
  }
}

export function AskNobiChatMessageThinking({
  connecting,
  pipelineStage,
}: AskNobiChatMessageThinkingProps): React.ReactNode {
  const label = usePipelineStageLabel(pipelineStage);

  if (connecting) {
    return (
      <output className="flex flex-row items-center gap-space-sm py-space-sm">
        <Spinner size="sm" />
      </output>
    );
  }

  return (
    <output className="flex flex-row items-center gap-space-sm py-space-sm">
      <motion.div
        animate={{
          opacity: [1, 0.7, 1],
        }}
        transition={{
          duration: 0.9,
          repeat: Infinity,
          ease: "easeInOut",
        }}
      >
        <motion.div
          animate={{
            scale: [1, 0.88, 1],
            rotate: [0, -4, 2, 0],
          }}
          transition={{
            duration: 1.2,
            repeat: Infinity,
            ease: "easeInOut",
          }}
        >
          <AskNobiChatLogo size="sm" />
        </motion.div>
      </motion.div>

      <motion.span
        key={label}
        animate={{ opacity: [0, 1] }}
        transition={{ duration: 0.3, ease: "easeOut" }}
        className="text-caption text-text-secondary"
      >
        {label}
      </motion.span>
    </output>
  );
}
